/**
 * Feature Manager - Centralized Feature Lifecycle Management
 *
 * Ensures consistent behavior for ALL feature operations:
 * - Add feature → Document + Scene + Tree + History
 * - Remove feature → Document + Scene + Tree + History
 * - Modify feature → Update + Recompute dependents + History
 *
 * This is the framework that makes parametric CAD work properly!
 */

class FeatureManager {
  constructor(cadViewer) {
    this.cadViewer = cadViewer;
    this.dependencyGraph = new Map(); // featureId → [dependent feature IDs]
  }

  /**
   * Get current document (always fresh reference)
   */
  get document() {
    return this.cadViewer.document;
  }

  /**
   * Add feature - CENTRAL METHOD for all feature creation
   * Ensures: Document + Scene + Tree + History + Dependencies + Parts
   */
  addFeature(feature, mesh = null) {
    console.log('[FeatureManager] Adding feature:', feature.name);

    const doc = this.document;
    if (!doc) {
      console.error('[FeatureManager] No document available');
      return null;
    }

    // 1. Add to document
    doc.addFeature(feature);

    // 2. Create Part from mesh and add to document.parts (for persistence)
    let part = null;
    if (mesh && this.cadViewer.createPartFromMesh) {
      part = this.cadViewer.createPartFromMesh(mesh, feature.id, feature.name);
      doc.parts.push(part);
      this.cadViewer.parts.set(part.id, part);
      doc.hasUnsavedChanges = true;
      console.log('[FeatureManager] Created Part:', part.id);
    }

    // 3. Add mesh to scene (if provided)
    if (mesh) {
      this.cadViewer.scene.add(mesh);
      this.cadViewer.cadObjects.set(feature.id, mesh);
    }

    // 4. Update feature tree
    this.cadViewer.updateFeatureTree();

    // 5. Record in history (include part data for proper undo/redo)
    if (this.cadViewer.historyManager) {
      this.cadViewer.historyManager.recordOperation({
        type: 'add_feature',
        name: `Create ${feature.name}`,
        featureId: feature.id,
        partId: part ? part.id : null,
        feature: JSON.parse(JSON.stringify(feature)),
        part: part ? JSON.parse(JSON.stringify(part)) : null,
        geometry: mesh ? this.cadViewer.historyManager._serializeGeometry(mesh) : null
      });
    }

    // 6. Update dependency graph
    this.updateDependencies(feature);

    console.log('[FeatureManager] ✓ Feature added completely (with Part)');
    return feature.id;
  }

  /**
   * Remove feature - CENTRAL METHOD for all feature deletion
   * Ensures: Document + Scene + Tree + History + Dependents + Parts
   */
  removeFeature(featureId) {
    console.log('[FeatureManager] Removing feature:', featureId);

    const doc = this.document;
    if (!doc) {
      console.error('[FeatureManager] No document available');
      return false;
    }

    const feature = doc.getFeature(featureId);
    if (!feature) {
      console.warn('[FeatureManager] Feature not found:', featureId);
      return false;
    }

    // Store for history
    const deletedFeature = JSON.parse(JSON.stringify(feature));
    const mesh = this.cadViewer.cadObjects.get(featureId);
    const deletedGeometry = mesh ? this.cadViewer.historyManager._serializeGeometry(mesh) : null;

    // Store part data before deletion
    const partIndex = doc.parts.findIndex(p => p.id === featureId);
    const deletedPart = partIndex !== -1 ? JSON.parse(JSON.stringify(doc.parts[partIndex])) : null;

    // 1. Remove from document
    doc.deleteFeature(featureId);

    // 2. Remove from parts array
    if (partIndex !== -1) {
      doc.parts.splice(partIndex, 1);
      this.cadViewer.parts.delete(featureId);
      doc.hasUnsavedChanges = true;
      console.log('[FeatureManager] Removed Part:', featureId);
    }

    // 3. Remove mesh from scene
    if (mesh) {
      this.cadViewer.scene.remove(mesh);
      if (mesh.geometry) mesh.geometry.dispose();
      if (mesh.material) {
        if (Array.isArray(mesh.material)) {
          mesh.material.forEach(m => m.dispose());
        } else {
          mesh.material.dispose();
        }
      }
      this.cadViewer.cadObjects.delete(featureId);
    }

    // 4. Update feature tree
    this.cadViewer.updateFeatureTree();

    // 5. Record in history (include part data)
    if (this.cadViewer.historyManager) {
      this.cadViewer.historyManager.recordOperation({
        type: 'delete_feature',
        name: `Delete ${deletedFeature.name}`,
        featureId: featureId,
        partId: deletedPart ? deletedPart.id : null,
        deletedFeature: deletedFeature,
        deletedPart: deletedPart,
        deletedGeometry: deletedGeometry
      });
    }

    // 5. Update dependent features (recompute features that depend on deleted one)
    this.recomputeDependents(featureId);

    console.log('[FeatureManager] ✓ Feature removed completely');
    return true;
  }

  /**
   * Modify feature - CENTRAL METHOD for all feature modifications
   * Ensures: Update + Recompute + Tree + History
   */
  modifyFeature(featureId, updates) {
    console.log('[FeatureManager] Modifying feature:', featureId);

    const feature = this.document.getFeature(featureId);
    if (!feature) return false;

    // Store old state for history
    const oldFeature = JSON.parse(JSON.stringify(feature));

    // 1. Update feature in document
    Object.assign(feature.parameters, updates);
    this.document.hasUnsavedChanges = true;

    // 2. Recreate mesh with new parameters
    this.recreateFeatureMesh(featureId);

    // 3. Update feature tree
    this.cadViewer.updateFeatureTree();

    // 4. Record in history
    if (this.cadViewer.historyManager) {
      this.cadViewer.historyManager.recordOperation({
        type: 'edit_feature',
        name: `Edit ${feature.name}`,
        featureId: featureId,
        oldState: oldFeature,
        newState: JSON.parse(JSON.stringify(feature))
      });
    }

    // 5. Recompute ALL dependent features (PARAMETRIC!)
    this.recomputeDependents(featureId);

    console.log('[FeatureManager] ✓ Feature modified completely');
    return true;
  }

  /**
   * Update dependency graph when feature is added
   */
  updateDependencies(feature) {
    // Clear existing dependencies for this feature
    this.dependencyGraph.delete(feature.id);

    // Build new dependencies
    const dependencies = [];

    // Check what this feature depends on
    if (feature.type === 'extrude' || feature.type === 'pocket') {
      if (feature.parameters.baseSketch) {
        dependencies.push(feature.parameters.baseSketch);
      }
    } else if (feature.type === 'revolve' || feature.type === 'groove') {
      if (feature.parameters.baseSketch) {
        dependencies.push(feature.parameters.baseSketch);
      }
    } else if (feature.type === 'fillet' || feature.type === 'chamfer') {
      if (feature.parameters.targetFeature) {
        dependencies.push(feature.parameters.targetFeature);
      }
    }

    // Store dependencies
    if (dependencies.length > 0) {
      this.dependencyGraph.set(feature.id, dependencies);
    }
  }

  /**
   * Recompute all features that depend on the given feature (PARAMETRIC UPDATE)
   */
  recomputeDependents(featureId) {
    console.log('[FeatureManager] Recomputing dependents of:', featureId);

    // Find all features that depend on this one
    const dependents = [];
    for (const [depId, dependencies] of this.dependencyGraph) {
      if (dependencies.includes(featureId)) {
        dependents.push(depId);
      }
    }

    // Recompute each dependent
    dependents.forEach(depId => {
      console.log('[FeatureManager] Recomputing dependent:', depId);
      this.recreateFeatureMesh(depId);

      // Recursively recompute its dependents
      this.recomputeDependents(depId);
    });
  }

  /**
   * Recreate feature mesh from feature data
   */
  recreateFeatureMesh(featureId) {
    const feature = this.document.getFeature(featureId);
    if (!feature) return;

    // Remove old mesh
    const oldMesh = this.cadViewer.cadObjects.get(featureId);
    if (oldMesh) {
      this.cadViewer.scene.remove(oldMesh);
      if (oldMesh.geometry) oldMesh.geometry.dispose();
      if (oldMesh.material) oldMesh.material.dispose();
      this.cadViewer.cadObjects.delete(featureId);
    }

    // Recreate based on feature type
    switch (feature.type) {
      case 'extrude':
        this._recreateExtrude(feature);
        break;
      case 'revolve':
        this._recreateRevolve(feature);
        break;
      case 'primitive':
        this._recreatePrimitive(feature);
        break;
      // Add more types as needed
    }
  }

  /**
   * Recreate extrude mesh
   */
  _recreateExtrude(feature) {
    const sketch = this.document.getFeature(feature.parameters.baseSketch);
    if (!sketch) return;

    const shape = this.cadViewer.convertSketchToShape(sketch.parameters.entities);
    if (!shape) return;

    const geometry = new THREE.ExtrudeGeometry(shape, {
      depth: feature.parameters.distance,
      bevelEnabled: false
    });

    const material = new THREE.MeshStandardMaterial({
      color: 0x808080,
      metalness: 0.3,
      roughness: 0.7
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.userData = {
      isCADFeature: true,
      featureId: feature.id,
      featureName: feature.name
    };

    this.cadViewer.scene.add(mesh);
    this.cadViewer.cadObjects.set(feature.id, mesh);
  }

  /**
   * Recreate revolve mesh
   */
  _recreateRevolve(feature) {
    // Similar to extrude but with LatheGeometry
    // Implementation here...
  }

  /**
   * Recreate primitive mesh
   */
  _recreatePrimitive(feature) {
    // Use feature.parameters to recreate the primitive
    // Implementation here...
  }
}

// Export
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { FeatureManager };
}
